import { mapState } from 'vuex';
import { readableFileSizeFromBytes } from '@/utils/vuex';
import { localizeDate } from '@/utils/date';
import { themesMixin, vuetifyColors } from '@/utils/themes';
import MarkdownItVueLight from 'markdown-it-vue/dist/markdown-it-vue-light.umd.min.js';
import formMixin from '../formMixin';
import get from 'lodash/get';
import set from 'lodash/set';
import moment from 'moment';
import ApiService from '@/services/apiService';

export default {
  components: {
    MarkdownItVueLight,
  },
  mixins: [formMixin, themesMixin],
  props: {
    gatewayData: {
      type: Object,
      required: true,
    },
  },
  data: function () {
    return {
      battery_level: false,
      battery_warning: false,
      statistics: false,
      loadingMetrics: false,
      selectedTimeFrame: { value: '24h', interval: 'hour', name: this.$i18n_t('NiotaClassic.Devices.details.last24') },
      timeFrames: [
        { value: '24h', interval: 'hour', name: this.$i18n_t('NiotaClassic.Devices.details.last24') },
        { value: '1w', interval: 'day', name: this.$i18n_t('NiotaClassic.Devices.details.lastWeek') },
        { value: '1M', interval: 'day', name: this.$i18n_t('NiotaClassic.Devices.details.lastMonth') },
      ],
      packetsBar: {},
      rssiLsnrGraph: {},
      sfGraph: {},
      sfGraphOptions: {
        legend: false,
        scales: {
          yAxes: [
            {
              ticks: {
                min: 7,
                max: 12,
                stepSize: 0.5,
              },
            },
          ],
        },
      },
      timeFormat: 'LT',
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    accountTitle() {
      return get(this.gatewayData, 'account.name') || null;
    },
    accountLink() {
      if (get(this.gatewayData, 'account.id')) {
        return `/account-mgmt/${get(this.gatewayData, 'account.id')}`;
      }
      return null;
    },
    n1Protocol() {
      return process.env.VUE_APP_N1_API_PROTOCOL;
    },
    n1Host() {
      return process.env.VUE_APP_N1_API_HOST;
    },
    isFirefly() {
      let type = get(this.gatewayData, 'datasource.type');
      return type === 'firefly_mqtt' || type === 'firefly';
    },
    gwWarningLevel() {
      return parseFloat(get(this.gatewayData, 'properties.threshold.green') || 5);
    },
    gwAlertLevel() {
      return parseFloat(get(this.gatewayData, 'properties.threshold.yellow') || 12);
    },
    gwLastPacketHours() {
      const lastPaket = get(this.gatewayData, 'lastPacket._source.received_at_server');
      // 36e5 aka 1 hr in ms
      const diff = Math.abs(new Date(lastPaket) - new Date()) / 36e5;
      return Math.round(diff * 100) / 100;
    },
    gwState() {
      const green = this.gwWarningLevel;
      const yellow = this.gwAlertLevel;
      const diff = this.gwLastPacketHours;
      if (diff < green) {
        return this.getColor('success');
      } else if (diff >= green && diff < yellow) {
        return this.getColor('warning');
      } else {
        return this.getColor('error');
      }
    },
  },
  watch: {
    color: function () {
      let packetsBarData = get(this.packetsBar, 'individual[0].data');
      let rssiLsnrGraphData = get(this.rssiLsnrGraph, 'individual[0].data');
      let sfGraphData = get(this.sfGraph, 'individual[0].data');
      if (packetsBarData && packetsBarData.length) {
        set(this.packetsBar, 'individual[0].color', this.vuetifyColorHex());
      }
      if (rssiLsnrGraphData && rssiLsnrGraphData.length) {
        set(this.rssiLsnrGraph, 'common.color', this.vuetifyColorHex());
      }
      if (sfGraphData && sfGraphData.length) {
        set(this.sfGraph, 'common.color', this.vuetifyColorHex());
      }
    },
    gatewayData() {
      this.getPacketMetricsData();
    },
    selectedTimeFrame: {
      handler(newVal) {
        this.timeFormat = newVal.interval !== 'hour' ? 'L' : 'LT';
      },
    },
  },
  created() {
    moment.locale(
      (this && this.$store ? this.$store.state.app.language : false) ||
        window.navigator.userLanguage ||
        window.navigator.language
    );
  },
  mounted() {
    this.getPacketMetricsData();
  },
  methods: {
    clickManufacturerFile(file) {
      window.open(this.getManualFileUrl(file.storagePath));
    },
    getManualFileUrl(image) {
      if (/^http(?:s):\/\//i.test(image)) {
        return image;
      }
      return `${process.env.VUE_APP_N1_API_PROTOCOL}://${process.env.VUE_APP_N1_API_HOST}${image}`;
    },
    readableFileSizeFromBytes(fileSize) {
      return readableFileSizeFromBytes(fileSize);
    },
    getColor(type) {
      return vuetifyColors[type];
    },
    localizeDate(date) {
      return localizeDate(date);
    },
    niota1ImageUrl(imagePath) {
      if (imagePath) {
        if (/^http(?:s):\/\//i.test(imagePath)) {
          return imagePath;
        }
        return `${this.n1Protocol}://${this.n1Host}${imagePath}`;
      }
      return '';
    },
    getDatasourceImage(item) {
      return get(item, 'datasource.image');
    },
    getFireflyOrganizationId(item) {
      return get(item, 'properties.fireflyGateway.organization_id');
    },
    getGwManagementToolLink(item) {
      return get(item, 'properties.linkToTool');
    },
    getOriginatorTitle(item) {
      return get(item, 'originator.title');
    },
    selectTimeFrame() {
      this.getPacketMetricsData();
    },
    loadAllMetrics(data) {
      this.loadingMetrics = true;
      ApiService.gwMgmtLoadAllMetrics(this.gatewayData.id, data)
        .then((res) => {
          if (res.packetMetrics || res.rssiLsnrSfMetrics) {
            this.setMetricsData(res);
          } else {
            this.$snack(this.$i18n_t('NiotaClassic.GWMonitoring.snacks.notFound'), 'info');
          }
        })
        .catch((error) => {
          this.$snack(this.$i18n_t('NiotaClassic.GWMonitoring.snacks.loadMetricsFailed'), 'error');
          console.error(error);
        })
        .finally(() => {
          this.loadingMetrics = false;
        });
    },
    getPacketMetricsData() {
      this.statistics = true;
      let data = {
        range: this.selectedTimeFrame.value,
        interval: this.selectedTimeFrame.interval,
      };
      this.loadAllMetrics(data);
    },
    setMetricsData(data) {
      let packetMetricsData = get(data, 'packetMetrics.data');
      let rssiMetricsData = get(data, 'rssiLsnrSfMetrics.data.rssi');
      let lsnrMetricsData = get(data, 'rssiLsnrSfMetrics.data.lsnr');
      let sfMetricsData = get(data, 'rssiLsnrSfMetrics.data.sf');
      // update packet Bar
      this.updateBar(packetMetricsData);
      // update rssi & lsnr Graph
      set(rssiMetricsData, 'label', this.$i18n_t('NiotaClassic.Devices.table.rssi'));
      set(lsnrMetricsData, 'label', this.$i18n_t('NiotaClassic.Devices.table.lsnr'));
      let graphData = [rssiMetricsData, lsnrMetricsData];
      this.updateRssiLsnrGraph(rssiMetricsData.labels, graphData);
      // update SF Graph
      this.updateSfGraph(sfMetricsData);
    },
    mapLabelsToLocalTime(labels) {
      return labels.map((date) => {
        return moment(new Date(date)).format(this.timeFormat);
      });
    },
    updateBar(datasets) {
      let graphData = {
        common: { color: false, labels: this.mapLabelsToLocalTime(datasets.labels) },
        individual: [],
      };
      graphData.individual.push({
        data: datasets.metricsData,
        label: this.$i18n_t('NiotaClassic.Devices.details.latestPackets'),
        color: this.vuetifyColorHex(),
      });
      this.packetsBar = graphData;
    },
    updateRssiLsnrGraph(labels, datasets) {
      let graphData = {
        common: { color: false, labels: this.mapLabelsToLocalTime(labels) },
        individual: [],
      };
      graphData.common.color = this.vuetifyColorHex();
      datasets.forEach((dataset) => {
        graphData.individual.push({
          data: dataset.data,
          label: dataset.label,
        });
      });
      this.rssiLsnrGraph = graphData;
    },
    updateSfGraph(datasets) {
      let graphData = {
        common: { color: false, labels: this.mapLabelsToLocalTime(datasets.labels) },
        individual: [],
      };
      graphData.common.color = this.vuetifyColorHex();
      graphData.individual.push({
        data: datasets.data,
        label: this.$i18n_t('NiotaClassic.Devices.table.fc'),
      });
      this.sfGraph = graphData;
    },
  },
};
